import { ref } from 'vue';
import type { AccessRequest } from '../types/users';
import PlaceholderImg from '@/assets/img/layouts/reload_placeholder.svg';
import { useAppState } from '@/core/store/useAppState';
import {
  type InvitationsQuery,
  Role,
  useAccessRequestAcceptMutation,
  useAccessRequestDeclineMutation,
  useDeleteUserMutation,
  useGetActiveAccessRequestsQuery,
  useGetUserQuery,
  useInvitationsQuery,
  useInviteUserToSupplierMutation,
  type UsersQuery,
  useSetInvitationRoleMutation,
  useSetUserRoleMutation,
  useUsersQuery,
} from '@/graphql/generated';

const users = ref<UsersQuery['users']>();
const accessRequests = ref<AccessRequest[]>();
const invitations = ref<InvitationsQuery['invitations']>();

export const useUsersStore = () => {
  const { showReloadPageAlert } = useAppState();

  const getUsers = async () => {
    const { request, data, response } = useUsersQuery({});

    await request();

    if (data.value) {
      users.value = data.value.users;
    }

    return response;
  };

  const getUsersInvitations = async () => {
    const { request } = useInvitationsQuery({});
    const response = await request();

    if (response?.data) {
      invitations.value = response.data.invitations;
    }

    return response;
  };

  const inviteUsers = async (emails: string[]) => {
    const { request } = useInviteUserToSupplierMutation({
      emails,
    });
    const response = await request();

    return response;
  };

  const getUser = async (id: string) => {
    const { request } = useGetUserQuery({ userId: id });

    const response = await request();

    return response;
  };

  const deleteUser = async (id: string) => {
    const { request, errors } = useDeleteUserMutation({ userId: id });

    const response = await request();

    if (!errors.value) {
      users.value = users.value?.filter((user) => user.id !== id);
    }

    return response;
  };

  const setUserRole = async (payload: { userId: string; role: Role }) => {
    const { request, errors, response } = useSetUserRoleMutation({ input: payload });

    await request();

    if (!errors.value && users.value) {
      users.value = users.value.map((item) => (item.id === payload.userId ? { ...item, role: payload.role } : item));
    }

    return response;
  };

  const setInvitationRole = async (payload: { invitationId: string; role: Role }) => {
    const { request } = useSetInvitationRoleMutation({ input: payload });

    const result = await request();

    if (!result?.errors && invitations.value) {
      invitations.value = invitations.value.map((item) =>
        item.id === payload.invitationId ? { ...item, role: payload.role } : item,
      );
    } else if (result.errors?.[0].__typename === 'InvitationNotFoundError') {
      showReloadPageAlert({
        icon: PlaceholderImg,
        title: 'Выбранное приглашение устарело или было принято пользователем',
        subtitle: 'Перезагрузите страницу для обновления данных',
        action: async () => {
          await Promise.all([getUsersInvitations(), getUsers()]);
        },
      });
    }

    return result;
  };

  const getActiveAccessRequests = async () => {
    const { request } = useGetActiveAccessRequestsQuery({});
    const response = await request();

    if (response?.data) {
      accessRequests.value = response.data.activeAccessRequests;
    }

    return response;
  };

  const processUpdateAccessRequestResult = (result: { errors?: { __typename: string }[] | null }, id: string) => {
    if (!result?.errors) {
      accessRequests.value = accessRequests.value?.filter((item) => item.id !== id);
    } else if (result.errors?.[0].__typename === 'AccessRequestInvalidStatusError') {
      showReloadPageAlert({
        icon: PlaceholderImg,
        title: 'Запрос на доступ устарел или был принят администратором',
        subtitle: 'Перезагрузите страницу для обновления данных',
        action: async () => {
          await Promise.all([getActiveAccessRequests(), getUsers(), getUsersInvitations()]);
        },
      });
    }
  };

  const declineAccessRequest = async (id: string) => {
    const { request } = useAccessRequestDeclineMutation({ input: { accessRequestId: id } });

    const response = await request();

    processUpdateAccessRequestResult(response, id);

    return response;
  };

  const accessRequestAccept = async (id: string) => {
    const { request } = useAccessRequestAcceptMutation({ input: { accessRequestId: id } });
    const response = await request();

    processUpdateAccessRequestResult(response, id);

    return response;
  };

  return {
    users,
    invitations,
    accessRequests,
    getUser,
    getUsers,
    getUsersInvitations,
    inviteUsers,
    deleteUser,
    setUserRole,
    getActiveAccessRequests,
    setInvitationRole,
    declineAccessRequest,
    accessRequestAccept,
  };
};
